import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AgentChatStart,
  AgentChatTurn,
  PreferenceAgent,
  PublicAgentConfig
} from '../../../models/preference-agent/preference-agent.model';
import { PreferenceAgentSerializer } from '../../../models/preference-agent/preference-agent-serializer.class';
import { BaseApiService } from '../base/base-api.service';
import { CustomErrorHandlerService } from '../custom-error-handler.service';

export interface PreferenceAgentPreviewToken {
  expiresAtUtc: string;
  token: string;
}

@Injectable()
export class PreferenceAgentsService extends BaseApiService<PreferenceAgent> {
  public constructor(httpClient: HttpClient) {
    // eslint-disable-next-line no-null/no-null
    super(httpClient, null, 'preferenceagents', new PreferenceAgentSerializer(), new CustomErrorHandlerService());
  }

  public getPreviewToken(id: string): Observable<PreferenceAgentPreviewToken> {
    return this.httpClient.get<PreferenceAgentPreviewToken>(`${this.url}/preferenceagents/${id}/preview-token`);
  }

  public publish(id: string): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/preferenceagents/${id}/publish`, {});
  }

  public unpublish(id: string): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/preferenceagents/${id}/unpublish`, {});
  }

  /**
   * PreferenceAgentController.Put returns 204 No Content on success (no body echoed back).
   * BaseApiService.update() would run that empty body through PreferenceAgentSerializer.fromJson,
   * which throws a TypeError on a null response — silently breaking the save and, because the
   * component's error handler never showed a toast, leaving the user stuck with a saved-but-marked-
   * dirty agent and no explanation. Re-emit what was sent instead of trusting the response body.
   */
  public override update(item: PreferenceAgent): Observable<PreferenceAgent> {
    return this.httpClient
      .put<PreferenceAgent | undefined>(`${this.url}/preferenceagents/${item.id}`, this.serializer.toJson(item))
      .pipe(
        map((data: PreferenceAgent | undefined): PreferenceAgent =>
          (data ? (this.serializer.fromJson(data) as PreferenceAgent) : item)),
        catchError((error: HttpErrorResponse): Observable<never> =>
          throwError((): HttpErrorResponse => this.errorHandler.tryParseError(error)))
      );
  }

  // The 3 public chat methods below hit anonymous endpoints whose wire shape is camelCase 1:1 with
  // the TS interfaces (PublicAgentConfig / AgentChatStart / AgentChatTurn), so raw httpClient is used
  // with no serializer/snake_case mapping involved.

  public getPublicAgent(tenantId: string, slug: string, previewToken?: string): Observable<PublicAgentConfig> {
    let params: HttpParams = new HttpParams();
    if (previewToken) {
      params = params.set('preview_token', previewToken);
    }
    return this.httpClient.get<PublicAgentConfig>(
      `${this.url}/preferenceagents/public/${tenantId}/${slug}`,
      { params }
    );
  }

  public startConversation(tenantId: string, slug: string, previewToken?: string): Observable<AgentChatStart> {
    let params: HttpParams = new HttpParams();
    if (previewToken) {
      params = params.set('preview_token', previewToken);
    }
    return this.httpClient.post<AgentChatStart>(
      `${this.url}/preferenceagents/public/${tenantId}/${slug}/conversations`,
      {},
      { params }
    );
  }

  public sendChatMessage(conversationId: string, message: string): Observable<AgentChatTurn> {
    return this.httpClient.post<AgentChatTurn>(
      `${this.url}/preferenceagents/public/conversations/${conversationId}/messages`,
      { message }
    );
  }

  public getFallbackPageUrl(tenantId: string, pageId: string): string {
    const encodedTenant: string = encodeURIComponent(tenantId);
    const encodedPage: string = encodeURIComponent(pageId);
    return `${this.url}/preferencepages/externalrender/${encodedTenant}/${encodedPage}?fullHtml=true&minify=false`;
  }
}
